<template>
  <div class="h-keywrap">
    <label v-if="label" class="h-key__label">{{ label }}</label>

    <div class="h-keyfield">
      <input
        ref="input"
        class="h-keyfield__input"
        :class="{ 'h-keyfield__input--masked': !revealed }"
        :type="revealed ? 'text' : 'password'"
        :value="value"
        readonly
        :aria-label="label || 'Secret key'"
        @focus="$event.target.select()"
      />
      <button type="button" class="h-keyfield__btn" @click="toggleReveal">
        <svg viewBox="0 0 24 24" class="h-keyfield__i" v-if="!revealed"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>
        <svg viewBox="0 0 24 24" class="h-keyfield__i" v-else><path d="M17.9 17.9A10.4 10.4 0 0 1 12 19c-7 0-11-7-11-7a19.8 19.8 0 0 1 5.1-5.9M9.9 4.2A10.5 10.5 0 0 1 12 5c7 0 11 7 11 7a19.8 19.8 0 0 1-2.2 3.2M1 1l22 22"/></svg>
        <span>{{ revealed ? 'Hide' : 'Reveal' }}</span>
      </button>
      <button type="button" class="h-keyfield__btn" @click="copy">
        <svg viewBox="0 0 24 24" class="h-keyfield__i" v-if="!copied"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        <svg viewBox="0 0 24 24" class="h-keyfield__i" v-else><path d="M20 6L9 17l-5-5"/></svg>
        <span>{{ copied ? 'Copied' : 'Copy' }}</span>
      </button>
    </div>

    <div v-if="shownOnce" class="h-key__warn" role="alert">
      <svg viewBox="0 0 24 24" class="h-key__warn-i"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>
      <span>{{ warning }}</span>
    </div>
    <div v-else-if="hint" class="h-key__hint">{{ hint }}</div>
  </div>
</template>

<script>
// Secret reveal/copy field — masked (blurred) until Reveal, with a copy button
// and, for shown-once secrets, an amber warning banner. Works for any secret
// (hk_live_… integration keys, hermes_live_… activation tokens).
// Matches the API-key reveal field in design/components/forms.html.
export default {
  name: 'HermesApiKeyRevealField',
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: 'Live secret key' },
    // Show the amber shown-once banner (secrets displayed once at creation).
    shownOnce: { type: Boolean, default: true },
    warning: {
      type: String,
      default: 'Copy this now — it is shown once and never again. Rotate it if it leaks.',
    },
    hint: { type: String, default: '' },
    // Start revealed (e.g. immediately after minting). Default masked.
    startRevealed: { type: Boolean, default: false },
  },
  data() {
    return { revealed: this.startRevealed, copied: false, copyTimer: null };
  },
  methods: {
    toggleReveal() {
      this.revealed = !this.revealed;
    },
    async copy() {
      const text = this.value || '';
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else if (this.$refs.input) {
          this.$refs.input.removeAttribute('readonly');
          this.$refs.input.type = 'text';
          this.$refs.input.select();
          document.execCommand('copy');
          this.$refs.input.setAttribute('readonly', 'readonly');
          if (!this.revealed) this.$refs.input.type = 'password';
        }
        this.flashCopied();
        this.$emit('copied');
      } catch (e) {
        // Non-fatal — leave the button unchanged.
      }
    },
    flashCopied() {
      this.copied = true;
      if (this.copyTimer) clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copied = false; }, 1600);
    },
  },
  emits: ['copied'],
  beforeUnmount() {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  },
};
</script>

<style scoped>
.h-keywrap { font-family: var(--h-font-body, system-ui); }
.h-key__label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; color: var(--h-text-primary, #0F172A); }
.h-keyfield {
  display: flex; align-items: stretch;
  border: 1px solid var(--h-border-strong, #CBD5E1);
  border-radius: var(--h-radius, 6px);
  overflow: hidden; background: var(--h-bg-card, #fff);
}
.h-keyfield__input {
  flex: 1; min-width: 0; border: none; background: transparent;
  font-family: var(--h-font-mono, monospace); font-size: 13px; padding: 9px 12px;
  color: var(--h-text-primary, #0F172A); letter-spacing: 0.02em;
}
.h-keyfield__input:focus { outline: none; }
/* Password type already masks with dots; add a slight blur wash as belt-and-braces. */
.h-keyfield__input--masked { filter: blur(0.15px); }
.h-keyfield__btn {
  border: none; border-left: 1px solid var(--h-border, #E2E8F0);
  background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #64748B);
  padding: 0 12px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 500;
  display: flex; align-items: center; gap: 5px; white-space: nowrap;
}
.h-keyfield__btn:hover { color: var(--h-accent-dark, #0369A1); }
.h-keyfield__btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-keyfield__i { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

.h-key__warn {
  display: flex; align-items: flex-start; gap: 8px; margin-top: 10px;
  background: var(--h-warning-bg, #FFFBEB); border: 1px solid var(--h-warning-border, #FDE68A);
  color: var(--h-warning-text, #B45309); border-radius: var(--h-radius, 6px);
  padding: 10px 12px; font-size: 12.5px; line-height: 1.4;
}
.h-key__warn-i { width: 15px; height: 15px; flex: none; stroke: currentColor; fill: none; stroke-width: 2; margin-top: 1px; }
.h-key__hint { margin-top: 6px; font-size: 12px; color: var(--h-text-tertiary, #94A3B8); }
</style>
